import React, { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'backlog' | 'in_progress' | 'review' | 'testing' | 'done';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignedTo: string | null;
  createdBy: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMNS: { id: Task['status']; label: string; emoji: string; color: string }[] = [
  { id: 'backlog',     label: 'Backlog',      emoji: '📋', color: '#4a4a7d' },
  { id: 'in_progress', label: 'In Progress',  emoji: '⚡', color: '#7c5c00' },
  { id: 'review',      label: 'Review',       emoji: '🔍', color: '#1a4a5c' },
  { id: 'testing',     label: 'Testing',      emoji: '🧪', color: '#2d4a1e' },
  { id: 'done',        label: 'Done',         emoji: '✅', color: '#1a3a2a' },
];

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#ff4444',
  high:     '#ff8800',
  medium:   '#ffcc00',
  low:      '#88cc44',
};

const AGENT_NAMES: Record<string, string> = {
  alice_pm:        'Alice',
  bob_frontend:    'Bob',
  charlie_backend: 'Charlie',
  diana_qa:        'Diana',
};

// ─── Component ────────────────────────────────────────────────────────────────

const KanbanBoard: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [recentlyMoved, setRecentlyMoved] = useState<string | null>(null);

  // Realtime subscription to tasks
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'projects/proj_crm/tasks'),
      (snap) => {
        const newTasks = snap.docs.map(d => ({ id: d.id, ...d.data() } as Task));
        setTasks(prev => {
          // Detect moved tasks for flash animation
          newTasks.forEach(t => {
            const old = prev.find(p => p.id === t.id);
            if (old && old.status !== t.status) {
              setRecentlyMoved(t.id);
              setTimeout(() => setRecentlyMoved(null), 1500);
            }
          });
          return newTasks;
        });
      }
    );
    return () => unsub();
  }, []);

  const tasksByStatus = (status: Task['status']) =>
    tasks.filter(t => t.status === status);

  return (
    <div style={{
      background: '#12122a',
      borderTop: '2px solid #4a4a7d',
      padding: '12px 16px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
      }}>
        <span style={{ fontSize: '14px' }}>📊</span>
        <span style={{
          color: '#FFD700',
          fontSize: '13px',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          letterSpacing: '0.05em',
        }}>
          CRM Nova Logistics — Task Board
        </span>
        <span style={{
          marginLeft: 'auto',
          color: '#888',
          fontSize: '11px',
          fontFamily: 'monospace',
        }}>
          {tasks.length} task{tasks.length !== 1 ? 's' : ''} · live
          <span style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            background: '#44ff88',
            borderRadius: '50%',
            marginLeft: '6px',
            verticalAlign: 'middle',
            boxShadow: '0 0 4px #44ff88',
          }} />
        </span>
      </div>

      {/* Columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '8px',
      }}>
        {COLUMNS.map(col => {
          const colTasks = tasksByStatus(col.id);
          return (
            <div key={col.id} style={{
              background: col.color + '55',
              border: `1px solid ${col.color}`,
              borderRadius: '6px',
              padding: '8px',
              minHeight: '80px',
            }}>
              {/* Column header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                marginBottom: '8px',
                borderBottom: `1px solid ${col.color}`,
                paddingBottom: '5px',
              }}>
                <span style={{ fontSize: '12px' }}>{col.emoji}</span>
                <span style={{
                  color: '#ddd',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                }}>
                  {col.label}
                </span>
                <span style={{
                  marginLeft: 'auto',
                  background: col.color,
                  color: '#fff',
                  borderRadius: '10px',
                  padding: '0 6px',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                }}>
                  {colTasks.length}
                </span>
              </div>

              {/* Task cards */}
              {colTasks.map(task => (
                <div
                  key={task.id}
                  style={{
                    background: recentlyMoved === task.id ? '#2a4a1a' : '#1e1e3a',
                    border: recentlyMoved === task.id
                      ? '1px solid #44ff88'
                      : '1px solid #333355',
                    borderRadius: '4px',
                    padding: '6px 8px',
                    marginBottom: '6px',
                    transition: 'all 0.4s ease',
                    boxShadow: recentlyMoved === task.id
                      ? '0 0 10px #44ff8866'
                      : 'none',
                  }}
                >
                  {/* Priority dot + title */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: PRIORITY_COLORS[task.priority] || '#888',
                      flexShrink: 0,
                      marginTop: '3px',
                    }} />
                    <span style={{
                      color: '#e0e0e0',
                      fontSize: '11px',
                      fontFamily: 'monospace',
                      lineHeight: '1.3',
                      wordBreak: 'break-word',
                    }}>
                      {task.title}
                    </span>
                  </div>

                  {/* Assignee */}
                  {task.assignedTo && (
                    <div style={{
                      marginTop: '5px',
                      color: '#aaa',
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                    }}>
                      👤 {AGENT_NAMES[task.assignedTo] || task.assignedTo}
                    </div>
                  )}
                </div>
              ))}

              {/* Empty state */}
              {colTasks.length === 0 && (
                <div style={{
                  color: '#555',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  textAlign: 'center',
                  paddingTop: '8px',
                }}>
                  empty
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
        flexWrap: 'wrap',
      }}>
        {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
          <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: c,
            }} />
            <span style={{ color: '#666', fontSize: '10px', fontFamily: 'monospace' }}>{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KanbanBoard;
